import { useMemo } from 'react';
import { useWorkspace } from './workspaceStore';
import type { WorkspaceLayout } from './types';

/**
 * Derives the API query params from all active workspace constraints.
 * Components should call this instead of manually reading state slices.
 */
export function useVisibleApiParams(): Record<string, any> {
  const projectContext = useWorkspace((s) => s.projectContext);
  const searchQuery = useWorkspace((s) => s.searchQuery);
  const locationFilter = useWorkspace((s) => s.locationFilter);
  const columnFilters = useWorkspace((s) => s.columnFilters);
  const filterModel = useWorkspace((s) => s.filterModel);
  const quickFilters = useWorkspace((s) => s.quickFilters);
  const sortModel = useWorkspace((s) => s.sortModel);
  const paginationModel = useWorkspace((s) => s.paginationModel);

  return useMemo(() => {
    const params: Record<string, any> = {};

    if (projectContext) {
      params.project = projectContext.projectName;
      if (projectContext.parentProjectName) {
        params.parent_project = projectContext.parentProjectName;
      }
    }

    if (searchQuery) params.query = searchQuery;

    if (locationFilter) {
      params.bbox_north = locationFilter.bounds.north;
      params.bbox_south = locationFilter.bounds.south;
      params.bbox_east = locationFilter.bounds.east;
      params.bbox_west = locationFilter.bounds.west;
    }

    if (columnFilters.jobName) params.query = columnFilters.jobName;
    if (columnFilters.project && !projectContext) params.project = columnFilters.project;
    if (columnFilters.status) params.status = columnFilters.status;
    if (columnFilters.owner) params.assigned_osp = columnFilters.owner;

    filterModel.items.forEach((item) => {
      if (item.value !== undefined && item.value !== '') {
        const fieldMap: Record<string, string> = {
          name: 'query',
          status: 'status',
          assignee: 'assigned_osp',
        };
        const apiField = fieldMap[item.field] ?? item.field;
        if (!params[apiField]) params[apiField] = item.value;
      }
    });

    if (quickFilters.myJobs) params.my_jobs = true;
    if (quickFilters.readyForPickup) params.ready_for_pickup = true;

    if (sortModel.length > 0) {
      params.sort_by = sortModel[0].field;
      params.sort_order = sortModel[0].sort;
    }

    params.page = paginationModel.page;
    params.page_size = paginationModel.pageSize;

    return params;
  }, [projectContext, searchQuery, locationFilter, columnFilters, filterModel, quickFilters, sortModel, paginationModel]);
}

/**
 * Returns the total count of active dataset constraints.
 * Includes spatial overlay and analyze session so ConstraintChipBar
 * accurately reflects all active constraints.
 */
export function useActiveConstraintCount(): number {
  const searchQuery = useWorkspace((s) => s.searchQuery);
  const locationFilter = useWorkspace((s) => s.locationFilter);
  const columnFilters = useWorkspace((s) => s.columnFilters);
  const filterModel = useWorkspace((s) => s.filterModel);
  const quickFilters = useWorkspace((s) => s.quickFilters);
  const projectContext = useWorkspace((s) => s.projectContext);
  const spatialOverlay = useWorkspace((s) => s.spatialOverlay);
  const analyzeStatus = useWorkspace((s) => s.analyzeSession.status);

  return useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (locationFilter) count++;
    if (projectContext) count++;
    Object.values(columnFilters).forEach((v) => { if (v) count++; });
    filterModel.items.forEach((i) => { if (i.value !== undefined && i.value !== '') count++; });
    if (quickFilters.myJobs) count++;
    if (quickFilters.readyForPickup) count++;
    if (spatialOverlay) count++;
    if (analyzeStatus !== 'idle') count++;
    return count;
  }, [searchQuery, locationFilter, columnFilters, filterModel, quickFilters, projectContext, spatialOverlay, analyzeStatus]);
}

export function useHasActiveConstraints(): boolean {
  return useActiveConstraintCount() > 0;
}

/**
 * Derives the current workspace layout from portal and analyze state.
 * Used by UISandboxPage to determine CSS grid template rows.
 */
export function useWorkspaceLayout(): WorkspaceLayout {
  const activePortal = useWorkspace((s) => s.activePortal);
  const analyzeStatus = useWorkspace((s) => s.analyzeSession.status);
  const inspectorOpen = useWorkspace((s) => s.analyzeSession.inspectorOpen);

  return useMemo(() => {
    const hasPortal = activePortal !== null;
    const hasAnalyze = analyzeStatus !== 'idle' && inspectorOpen;
    if (hasPortal && hasAnalyze) return 'portal+analyze';
    if (hasPortal) return 'portal';
    if (hasAnalyze) return 'analyze';
    return 'default';
  }, [activePortal, analyzeStatus, inspectorOpen]);
}
